/**
 * Merken / Merklisten – Stufe 1 (rein lokal, kein Server)
 *
 * Speichert mehrere Merklisten im localStorage. Produktdaten (Preis, Bild,
 * Verfügbarkeit) werden bewusst NICHT mitgespeichert, sondern immer frisch über
 * /products/{handle}.js geholt – damit veralten sie nie und geteilte Listen
 * funktionieren beim Empfänger ohne Server.
 *
 * Die Struktur ist so angelegt, dass Stufe 2 (Cloudflare Worker + App Proxy für
 * Account-Sync) nur andockt: stabile Listen-IDs, updatedAt je Liste, Versionsfeld.
 *
 * Öffentliche API: window.SOWishlist
 * Event: document -> 'wishlist:change'
 */
(function () {
  'use strict';

  var KEY = 'solaroutlet_wishlist_v1';
  var VERSION = 1;
  var SHARE_PARAM = 's';
  var SHARE_MAX = 1800;

  /* ---------------------------------------------------------------- Speicher */

  // localStorage kann fehlen (Privatmodus, gesperrte Cookies) – dann bleibt die
  // Funktion still deaktiviert, statt den Shop mit Fehlern zu belasten.
  var store = (function () {
    try {
      var probe = '__wl__';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return window.localStorage;
    } catch (e) {
      return null;
    }
  })();

  function emptyState() {
    return { v: VERSION, activeListId: null, lists: [] };
  }

  function read() {
    if (!store) return emptyState();
    try {
      var raw = store.getItem(KEY);
      if (!raw) return emptyState();
      var data = JSON.parse(raw);
      if (!data || typeof data !== 'object' || !Array.isArray(data.lists)) return emptyState();
      data.v = data.v || VERSION;
      data.lists.forEach(function (l) {
        if (!Array.isArray(l.items)) l.items = [];
      });
      return data;
    } catch (e) {
      return emptyState();
    }
  }

  function write(state) {
    if (!store) return state;
    try {
      store.setItem(KEY, JSON.stringify(state));
    } catch (e) {
      // Quota o. ä. – Zustand bleibt im Speicher der Seite gültig.
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { state: state } }));
    return state;
  }

  /* ------------------------------------------------------------------ Helfer */

  function now() {
    return Date.now();
  }

  function uid() {
    return 'wl_' + Math.random().toString(36).slice(2, 8) + now().toString(36).slice(-4);
  }

  function defaultName() {
    var d = new Date();
    var p = function (n) {
      return String(n).padStart(2, '0');
    };
    return 'Meine Merkliste vom ' + p(d.getDate()) + '.' + p(d.getMonth() + 1) + '.' + d.getFullYear();
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function money(cents) {
    var v = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);
    } catch (e) {
      return v.toFixed(2).replace('.', ',') + ' €';
    }
  }

  // Shopify liefert Bild-URLs protokollrelativ ("//cdn.shopify.com/…").
  // Ohne explizites https: wird daraus auf http-Seiten eine http-URL, die das
  // CDN nicht ausliefert. Zusätzlich wird der Query-Trenner korrekt gewählt.
  function imgUrl(src, width) {
    if (!src) return '';
    var u = src.indexOf('//') === 0 ? 'https:' + src : src;
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function rootUrl() {
    var r = (window.theme && window.theme.routes && window.theme.routes.root_url) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  /* ------------------------------------------------------------ Listen-Logik */

  function getState() {
    return read();
  }

  function getLists() {
    return read().lists;
  }

  function findList(state, id) {
    for (var i = 0; i < state.lists.length; i++) {
      if (state.lists[i].id === id) return state.lists[i];
    }
    return null;
  }

  function ensureActive(state) {
    var list = findList(state, state.activeListId);
    if (list) return list;
    if (state.lists.length) {
      state.activeListId = state.lists[0].id;
      return state.lists[0];
    }
    var fresh = { id: uid(), name: defaultName(), createdAt: now(), updatedAt: now(), items: [] };
    state.lists.push(fresh);
    state.activeListId = fresh.id;
    return fresh;
  }

  function getActiveList() {
    var state = read();
    var list = findList(state, state.activeListId);
    return list || (state.lists.length ? state.lists[0] : null);
  }

  function createList(name) {
    var state = read();
    var list = {
      id: uid(),
      name: (name || '').trim() || defaultName(),
      createdAt: now(),
      updatedAt: now(),
      items: []
    };
    state.lists.push(list);
    state.activeListId = list.id;
    write(state);
    return list;
  }

  function renameList(id, name) {
    var state = read();
    var list = findList(state, id);
    if (!list) return false;
    list.name = (name || '').trim() || list.name;
    list.updatedAt = now();
    write(state);
    return true;
  }

  function deleteList(id) {
    var state = read();
    state.lists = state.lists.filter(function (l) {
      return l.id !== id;
    });
    if (state.activeListId === id) state.activeListId = state.lists.length ? state.lists[0].id : null;
    write(state);
    return true;
  }

  function setActiveList(id) {
    var state = read();
    if (!findList(state, id)) return false;
    state.activeListId = id;
    write(state);
    return true;
  }

  /* -------------------------------------------------------------- Positionen */

  function has(handle, listId) {
    var state = read();
    var list = listId ? findList(state, listId) : findList(state, state.activeListId);
    if (!list) return false;
    return list.items.some(function (i) {
      return i.handle === handle;
    });
  }

  // In irgendeiner Liste? (fürs Herz sinnvoll: einmal gemerkt = gemerkt)
  function hasAnywhere(handle) {
    return read().lists.some(function (l) {
      return l.items.some(function (i) {
        return i.handle === handle;
      });
    });
  }

  function add(handle, productId, listId) {
    if (!handle) return false;
    var state = read();
    var list = listId ? findList(state, listId) : ensureActive(state);
    if (!list) return false;
    if (
      !list.items.some(function (i) {
        return i.handle === handle;
      })
    ) {
      list.items.push({
        handle: handle,
        id: productId ? Number(productId) : null,
        variantId: null,
        qty: 1,
        addedAt: now()
      });
      list.updatedAt = now();
    }
    write(state);
    return true;
  }

  function remove(handle, listId) {
    var state = read();
    var lists = listId ? [findList(state, listId)] : state.lists;
    lists.forEach(function (list) {
      if (!list) return;
      var before = list.items.length;
      list.items = list.items.filter(function (i) {
        return i.handle !== handle;
      });
      if (list.items.length !== before) list.updatedAt = now();
    });
    write(state);
    return true;
  }

  function setQty(handle, qty, listId) {
    var state = read();
    var list = listId ? findList(state, listId) : findList(state, state.activeListId);
    if (!list) return false;
    list.items.forEach(function (i) {
      if (i.handle === handle) i.qty = Math.max(1, parseInt(qty, 10) || 1);
    });
    list.updatedAt = now();
    write(state);
    return true;
  }

  // Herz-Klick: entfernt aus allen Listen, wenn schon gemerkt.
  function toggle(handle, productId) {
    if (hasAnywhere(handle)) {
      remove(handle);
      return false;
    }
    add(handle, productId);
    return true;
  }

  function count(listId) {
    var state = read();
    var list = listId ? findList(state, listId) : findList(state, state.activeListId);
    return list ? list.items.length : 0;
  }

  function countAll() {
    return read().lists.reduce(function (n, l) {
      return n + l.items.length;
    }, 0);
  }

  /* ---------------------------------------------------------- Produktabruf */

  var productCache = {};

  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch(rootUrl() + 'products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .catch(function () {
        return null; // Produkt gelöscht/deaktiviert -> Aufrufer zeigt Hinweis
      });
    return productCache[handle];
  }

  /* -------------------------------------------------------------- Teilen */

  function b64urlEncode(str) {
    var bytes = new TextEncoder().encode(str);
    var bin = '';
    bytes.forEach(function (b) {
      bin += String.fromCharCode(b);
    });
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function b64urlDecode(str) {
    var s = String(str).replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    var bin = atob(s);
    var bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  }

  function encodeShare(listId) {
    var state = read();
    var list = findList(state, listId) || findList(state, state.activeListId);
    if (!list || !list.items.length) return null;
    var payload = {
      n: list.name,
      i: list.items.map(function (it) {
        return it.handle;
      })
    };
    return b64urlEncode(JSON.stringify(payload));
  }

  function shareUrl(listId, pageUrl) {
    var code = encodeShare(listId);
    if (!code) return null;
    var base = pageUrl || rootUrl() + 'pages/merkliste';
    var url = window.location.origin + base + '?' + SHARE_PARAM + '=' + code;
    return url.length > SHARE_MAX ? { tooLong: true, url: url } : { tooLong: false, url: url };
  }

  function decodeShare(code) {
    try {
      var data = JSON.parse(b64urlDecode(code));
      if (!data || !Array.isArray(data.i)) return null;
      return { name: String(data.n || 'Geteilte Merkliste'), handles: data.i.filter(Boolean).map(String) };
    } catch (e) {
      return null;
    }
  }

  function importShared(shared) {
    if (!shared || !shared.handles.length) return null;
    var list = createList(shared.name);
    var state = read();
    var target = findList(state, list.id);
    shared.handles.forEach(function (h) {
      target.items.push({ handle: h, id: null, variantId: null, qty: 1, addedAt: now() });
    });
    target.updatedAt = now();
    write(state);
    return target;
  }

  /* ------------------------------------------------------------ Warenkorb */

  function addAllToCart(listId) {
    var list = listId ? findList(read(), listId) : getActiveList();
    if (!list || !list.items.length) return Promise.resolve({ added: 0, skipped: [] });

    return Promise.all(
      list.items.map(function (it) {
        return fetchProduct(it.handle).then(function (p) {
          return { item: it, product: p };
        });
      })
    ).then(function (rows) {
      var payload = [];
      var skipped = [];
      rows.forEach(function (row) {
        var p = row.product;
        if (!p) {
          skipped.push(row.item.handle);
          return;
        }
        var variant = (p.variants || []).filter(function (v) {
          return v.available;
        })[0];
        if (!variant) {
          skipped.push(p.title);
          return;
        }
        payload.push({ id: variant.id, quantity: Math.max(1, row.item.qty || 1) });
      });

      if (!payload.length) return { added: 0, skipped: skipped };

      var url = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add';
      return fetch(url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: payload })
      })
        .then(function (r) {
          return r.json();
        })
        .then(function () {
          return { added: payload.length, skipped: skipped };
        })
        .catch(function () {
          return { added: 0, skipped: skipped, error: true };
        });
    });
  }

  /* ------------------------------------------------------------------- API */

  var api = {
    available: !!store,
    getState: getState,
    getLists: getLists,
    getActiveList: getActiveList,
    createList: createList,
    renameList: renameList,
    deleteList: deleteList,
    setActiveList: setActiveList,
    has: has,
    hasAnywhere: hasAnywhere,
    add: add,
    remove: remove,
    setQty: setQty,
    toggle: toggle,
    count: count,
    countAll: countAll,
    fetchProduct: fetchProduct,
    encodeShare: encodeShare,
    shareUrl: shareUrl,
    decodeShare: decodeShare,
    importShared: importShared,
    addAllToCart: addAllToCart,
    money: money,
    imgUrl: imgUrl,
    SHARE_PARAM: SHARE_PARAM
  };
  window.SOWishlist = api;
  // Signal für Skripte, die vor dieser Datei ausgeführt werden (z. B. das
  // Seiten-Skript, das im Seiteninhalt und damit früher im DOM steht).
  document.dispatchEvent(new CustomEvent('wishlist:ready'));

  /* --------------------------------------------------- Element: Herz-Button */

  if (!customElements.get('wishlist-button')) {
    customElements.define(
      'wishlist-button',
      class extends HTMLElement {
        connectedCallback() {
          if (!api.available) {
            this.hidden = true;
            return;
          }
          this.handle = this.dataset.handle;
          this.productId = this.dataset.productId;
          this.btn = this.querySelector('button');
          if (!this.btn || !this.handle) return;

          this.onClick = this.onClick.bind(this);
          this.sync = this.sync.bind(this);
          this.btn.addEventListener('click', this.onClick);
          document.addEventListener('wishlist:change', this.sync);
          this.sync();
        }

        disconnectedCallback() {
          document.removeEventListener('wishlist:change', this.sync);
        }

        onClick(e) {
          e.preventDefault();
          e.stopPropagation();
          // Bewusst ohne Drawer: das Herz merkt still, der Zustand ist am
          // gefüllten Herz und am Punkt im Header ablesbar.
          var gemerkt = api.toggle(this.handle, this.productId);
          // Nach dem Merken pulst das Herz im Header kurz, damit erkennbar
          // ist, wohin das Produkt gewandert ist. Beim Entfernen nicht, dort
          // gibt es nichts zu zeigen.
          if (gemerkt) {
            pulsImHeader();
            zeigeHinweis();
          }
        }

        sync() {
          var on = api.hasAnywhere(this.handle);
          this.btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          this.btn.setAttribute('aria-label', on ? 'Gemerkt – entfernen' : 'Merken');
          this.btn.setAttribute('title', on ? 'Gemerkt – entfernen' : 'Merken');
          this.classList.toggle('is-active', on);
          // Variante mit Beschriftung (z. B. unter dem Warenkorb-Button)
          var label = this.querySelector('[data-wl-label]');
          if (label) label.textContent = on ? 'Gemerkt' : 'Merken';
        }
      }
    );
  }

  /* -------------------------------------------------- Puls im Kopfbereich */

  function pulsImHeader() {
    var ziel = document.querySelector('.thb-secondary-wishlist');
    if (!ziel) return;
    // Klasse neu setzen, damit die Animation auch beim zweiten Mal laeuft
    ziel.classList.remove('wishlist-puls');
    void ziel.offsetWidth;
    ziel.classList.add('wishlist-puls');
    window.setTimeout(function () {
      ziel.classList.remove('wishlist-puls');
    }, 900);
  }

  var hinweisFeld = null;
  var hinweisTimer = null;

  /* Hinweisfeld unten rechts, Machart wie der Sticky-Warenkorbknopf. Es wird
     einmal angelegt und danach nur ein- und ausgeblendet. */
  function zeigeHinweis() {
    if (!hinweisFeld) {
      hinweisFeld = document.createElement('a');
      hinweisFeld.className = 'wishlist-hinweis';
      hinweisFeld.href = '/pages/merkliste';
      hinweisFeld.innerHTML =
        '<svg viewBox="0 0 23 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<path d="M20.67 2.46412C20.2561 2.00144 19.7579 1.63313 19.2063 1.38144C18.6547 1.12977 18.0606 1 17.4605 1C16.8604 1 16.2663 1.12977 15.7147 1.38144C15.1631 1.63313 14.6649 2.00144 14.251 2.46412L11.5 5.49273L8.749 2.46412C8.33507 2.00144 7.8369 1.63313 7.2853 1.38144C6.73363 1.12977 6.13968 1 5.5395 1C4.93932 1 4.34537 1.12977 3.7937 1.38144C3.24201 1.63313 2.74405 2.00144 2.33 2.46412C1.47633 3.41627 1 4.682 1 5.99834C1 7.31493 1.47633 8.58059 2.33 9.53269L10.5223 18.5543C10.6486 18.6951 10.8002 18.8074 10.9682 18.884C11.1362 18.9605 11.3173 19 11.5 19C11.6827 19 11.8638 18.9605 12.0318 18.884C12.1998 18.8074 12.3514 18.6951 12.4777 18.5543L20.67 9.53269C21.5238 8.58059 22 7.31493 22 5.99834C22 4.682 21.5238 3.41627 20.67 2.46412Z" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>' +
        '</svg>' +
        '<span class="wishlist-hinweis__text">Dein Produkt ist in deiner <strong>Merkliste</strong></span>';
      document.body.appendChild(hinweisFeld);
    }
    window.clearTimeout(hinweisTimer);
    // Kurz warten, sonst faehrt es beim ersten Mal nicht herein, weil Anlegen
    // und Klasse im selben Zug passieren. Bewusst ein Timeout statt
    // requestAnimationFrame: der Browser drosselt rAF in Fenstern, die gerade
    // nicht im Vordergrund sind, und dann bliebe das Feld ausgeblendet.
    window.setTimeout(function () {
      hinweisFeld.classList.add('ist-sichtbar');
    }, 30);
    hinweisTimer = window.setTimeout(function () {
      hinweisFeld.classList.remove('ist-sichtbar');
    }, 4000);
  }

  /* ------------------------------------------------------ Element: Zähler */

  if (!customElements.get('wishlist-count')) {
    customElements.define(
      'wishlist-count',
      class extends HTMLElement {
        connectedCallback() {
          this.sync = this.sync.bind(this);
          document.addEventListener('wishlist:change', this.sync);
          this.sync();
        }
        disconnectedCallback() {
          document.removeEventListener('wishlist:change', this.sync);
        }
        // Kein Zahlenbadge: nur ein Punkt, wenn überhaupt etwas gemerkt ist.
        sync() {
          this.classList.toggle('is-empty', api.countAll() === 0);
        }
      }
    );
  }

})();
